import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiFileText,
    FiPlus,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    cancelQuotation,
    confirmQuotation,
    convertQuotation,
    deleteQuotation,
    duplicateQuotation,
    fetchQuotationDropdowns,
    fetchQuotationSummary,
    fetchQuotations,
    quotationDropdownsSelector,
    quotationFiltersSelector,
    quotationListSelector,
    quotationLoadingSelector,
    quotationOperationSelector,
    quotationPaginationSelector,
    quotationSummarySelector,
    resetQuotationFilters,
    setQuotationFilters,
    setQuotationLimit,
    setQuotationPage,
} from "../../../../../../store/billing";

import QuotationFilter from "./QuotationFilter";
import QuotationTable from "./QuotationTable";
import DocumentConversionDialog from "../shared/DocumentConversionDialog";
import "./QuotationPage.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getQuotationId = (
    quotation
) => {
    return (
        quotation?._id ||
        quotation?.id ||
        quotation?.quotationId ||
        ""
    );
};

const getQuotationNumber = (
    quotation
) => {
    return (
        quotation?.documentNumber ||
        quotation?.quotationNumber ||
        quotation?.quotationNo ||
        quotation?.number ||
        "this quotation"
    );
};

const formatCurrency = (
    value,
    currency = "INR"
) => {
    const amount =
        Number(value || 0);

    try {
        return new Intl.NumberFormat(
            "en-IN",
            {
                style: "currency",
                currency,
                maximumFractionDigits: 2,
            }
        ).format(amount);
    } catch (error) {
        return `₹${amount.toLocaleString(
            "en-IN"
        )}`;
    }
};

const getSummaryValue = (
    summary,
    fields,
    fallback = 0
) => {
    for (const field of fields) {
        const value =
            summary?.[field];

        if (
            value !== undefined &&
            value !== null &&
            value !== ""
        ) {
            return value;
        }
    }

    return fallback;
};

/*
|--------------------------------------------------------------------------
| QUOTATION PAGE
|--------------------------------------------------------------------------
*/

const QuotationPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    /*
    |--------------------------------------------------------------------------
    | REDUX DATA
    |--------------------------------------------------------------------------
    */

    const quotations =
        useSelector(
            quotationListSelector
        ) || [];

    const summary =
        useSelector(
            quotationSummarySelector
        ) || {};

    const filters =
        useSelector(
            quotationFiltersSelector
        ) || {};

    const pagination =
        useSelector(
            quotationPaginationSelector
        ) || {};

    const dropdowns =
        useSelector(
            quotationDropdownsSelector
        ) || {};

    const loadingState =
        useSelector(
            quotationLoadingSelector
        ) || {};

    const operation =
        useSelector(
            quotationOperationSelector
        ) || {};

    /*
    |--------------------------------------------------------------------------
    | LOCAL STATE
    |--------------------------------------------------------------------------
    */

    const [
        activeAction,
        setActiveAction,
    ] = useState(null);
    const [
        conversionDialog,
        setConversionDialog,
    ] = useState({
        open: false,
        document: null,
    });

    const listLoading =
        Boolean(
            loadingState.listLoading ||
            loadingState.fetchLoading
        );

    const summaryLoading =
        Boolean(
            loadingState.summaryLoading
        );

    /*
    |--------------------------------------------------------------------------
    | FILTER NORMALIZATION
    |--------------------------------------------------------------------------
    */

    const getRequestFilters =
        useCallback(
            (
                overrides = {}
            ) => {
                const requestFilters = {
                    ...filters,
                    ...overrides,
                };

                return Object.entries(
                    requestFilters
                ).reduce(
                    (
                        result,
                        [
                            key,
                            value,
                        ]
                    ) => {
                        if (
                            value !== "" &&
                            value !== null &&
                            value !== undefined
                        ) {
                            result[key] =
                                value;
                        }

                        return result;
                    },
                    {}
                );
            },
            [filters]
        );

    /*
    |--------------------------------------------------------------------------
    | LOAD DATA
    |--------------------------------------------------------------------------
    */

    const loadQuotations =
        useCallback(
            async (
                overrides = {}
            ) => {
                const requestFilters =
                    getRequestFilters(
                        overrides
                    );

                await Promise.all([
                    dispatch(
                        fetchQuotations(
                            requestFilters
                        )
                    ),

                    dispatch(
                        fetchQuotationSummary(
                            requestFilters
                        )
                    ),
                ]);
            },
            [
                dispatch,
                getRequestFilters,
            ]
        );

    useEffect(() => {
        dispatch(
            fetchQuotationDropdowns()
        );

        loadQuotations();
    }, [
        dispatch,
        loadQuotations,
    ]);

    /*
    |--------------------------------------------------------------------------
    | SUMMARY CARDS
    |--------------------------------------------------------------------------
    */

    const summaryCards =
        useMemo(
            () => [
                {
                    key:
                        "total",

                    label:
                        "Total Quotations",

                    value:
                        getSummaryValue(
                            summary,
                            [
                                "totalCount",
                                "documentCount",
                                "quotationCount",
                                "count",
                            ]
                        ),

                    className:
                        "quotation-summary-card-total",
                },

                {
                    key:
                        "draft",

                    label:
                        "Draft",

                    value:
                        getSummaryValue(
                            summary,
                            [
                                "draftCount",
                                "statusCounts.DRAFT",
                            ]
                        ),

                    className:
                        "quotation-summary-card-draft",
                },

                {
                    key:
                        "confirmed",

                    label:
                        "Confirmed",

                    value:
                        getSummaryValue(
                            summary,
                            [
                                "confirmedCount",
                                "statusCounts.CONFIRMED",
                            ]
                        ),

                    className:
                        "quotation-summary-card-confirmed",
                },

                {
                    key:
                        "cancelled",

                    label:
                        "Cancelled",

                    value:
                        getSummaryValue(
                            summary,
                            [
                                "cancelledCount",
                                "statusCounts.CANCELLED",
                            ]
                        ),

                    className:
                        "quotation-summary-card-cancelled",
                },

                {
                    key:
                        "amount",

                    label:
                        "Total Amount",

                    value:
                        formatCurrency(
                            getSummaryValue(
                                summary,
                                [
                                    "totalAmount",
                                    "grandTotal",
                                    "quotationAmount",
                                ]
                            )
                        ),

                    className:
                        "quotation-summary-card-amount",

                    isAmount:
                        true,
                },
            ],
            [summary]
        );

    /*
    |--------------------------------------------------------------------------
    | NAVIGATION
    |--------------------------------------------------------------------------
    */

    const handleCreateQuotation =
        () => {
            history.push(
                "/billing/quotations/create"
            );
        };

    const handleView =
        (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            history.push(
                `/billing/quotations/${quotationId}`
            );
        };

    const handleEdit =
        (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            history.push(
                `/billing/quotations/${quotationId}/edit`
            );
        };

    const handlePrint =
        (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            history.push(
                `/billing/quotations/${quotationId}/print`
            );
        };

    /*
    |--------------------------------------------------------------------------
    | FILTER HANDLERS
    |--------------------------------------------------------------------------
    */

    const handleFilterChange =
        (
            key,
            value,
            updatedFilters
        ) => {
            dispatch(
                setQuotationFilters(
                    updatedFilters || {
                        ...filters,
                        [key]:
                            value,
                        page:
                            1,
                    }
                )
            );
        };

    const handleApplyFilters =
        (
            appliedFilters
        ) => {
            const nextFilters = {
                ...appliedFilters,
                page:
                    1,
            };

            dispatch(
                setQuotationFilters(
                    nextFilters
                )
            );

            loadQuotations(
                nextFilters
            );
        };

    const handleResetFilters =
        (
            resetFilters
        ) => {
            dispatch(
                resetQuotationFilters()
            );

            loadQuotations({
                ...resetFilters,
                page:
                    1,
            });
        };

    const handleRefresh =
        () => {
            loadQuotations();
        };

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const handlePageChange =
        (
            page
        ) => {
            dispatch(
                setQuotationPage(
                    page
                )
            );

            loadQuotations({
                page,
            });
        };

    const handleLimitChange =
        (
            event
        ) => {
            const limit =
                Number(
                    event?.target
                        ?.value ||
                    event
                ) || 10;

            dispatch(
                setQuotationLimit(
                    limit
                )
            );

            loadQuotations({
                page:
                    1,
                limit,
            });
        };

    /*
    |--------------------------------------------------------------------------
    | CONFIRM QUOTATION
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            const quotationNumber =
                getQuotationNumber(
                    quotation
                );

            const approved =
                window.confirm(
                    `Are you sure you want to confirm ${quotationNumber}?`
                );

            if (!approved) {
                return;
            }

            setActiveAction({
                id:
                    quotationId,

                type:
                    "confirm",
            });

            try {
                const result =
                    await dispatch(
                        confirmQuotation({
                            documentId:
                                quotationId,

                            payload: {
                                payments:
                                    [],
                            },
                        })
                    );

                if (
                    result?.meta
                        ?.requestStatus ===
                    "fulfilled"
                ) {
                    await loadQuotations();
                }
            } finally {
                setActiveAction(
                    null
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CANCEL QUOTATION
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        async (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            const reason =
                window.prompt(
                    "Please enter the quotation cancellation reason:"
                );

            if (
                reason === null
            ) {
                return;
            }

            if (
                !reason.trim()
            ) {
                window.alert(
                    "Cancellation reason is required."
                );

                return;
            }

            setActiveAction({
                id:
                    quotationId,

                type:
                    "cancel",
            });

            try {
                const result =
                    await dispatch(
                        cancelQuotation({
                            documentId:
                                quotationId,

                            reason:
                                reason.trim(),

                            payload: {
                                reason:
                                    reason.trim(),
                            },
                        })
                    );

                if (
                    result?.meta
                        ?.requestStatus ===
                    "fulfilled"
                ) {
                    await loadQuotations();
                }
            } finally {
                setActiveAction(
                    null
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | DUPLICATE QUOTATION
    |--------------------------------------------------------------------------
    */

    const handleDuplicate =
        async (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            const quotationNumber =
                getQuotationNumber(
                    quotation
                );

            const approved =
                window.confirm(
                    `Create a duplicate of ${quotationNumber}?`
                );

            if (!approved) {
                return;
            }

            setActiveAction({
                id:
                    quotationId,

                type:
                    "duplicate",
            });

            try {
                const result =
                    await dispatch(
                        duplicateQuotation({
                            documentId:
                                quotationId,
                        })
                    );

                if (
                    result?.meta
                        ?.requestStatus ===
                    "fulfilled"
                ) {
                    await loadQuotations();
                }
            } finally {
                setActiveAction(
                    null
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CONVERT QUOTATION
    |--------------------------------------------------------------------------
    */

    const handleConvert =
        async (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            const allowedTargets =
                Array.isArray(
                    dropdowns
                        ?.conversionTargets
                )
                    ? dropdowns
                        .conversionTargets
                    : [];

            const defaultTarget =
                allowedTargets[0] ||
                "PROFORMA_INVOICE";

            const targetType =
                window.prompt(
                    `Convert quotation to which document type?\n\nAllowed: ${allowedTargets.length >
                        0
                        ? allowedTargets.join(
                            ", "
                        )
                        : "PROFORMA_INVOICE"
                    }`,
                    defaultTarget
                );

            if (
                targetType === null
            ) {
                return;
            }

            const normalizedTarget =
                targetType
                    .trim()
                    .toUpperCase()
                    .replace(
                        /\s+/g,
                        "_"
                    );

            if (
                !normalizedTarget
            ) {
                window.alert(
                    "Conversion target is required."
                );

                return;
            }

            if (
                allowedTargets.length >
                0 &&
                !allowedTargets.includes(
                    normalizedTarget
                )
            ) {
                window.alert(
                    `Invalid conversion target. Allowed values: ${allowedTargets.join(
                        ", "
                    )}`
                );

                return;
            }

            setActiveAction({
                id:
                    quotationId,

                type:
                    "convert",
            });

            try {
                const result =
                    await dispatch(
                        convertQuotation({
                            documentId:
                                quotationId,

                            payload: {
                                targetType:
                                    normalizedTarget,

                                saveAsDraft:
                                    true,

                                payments:
                                    [],
                            },
                        })
                    );

                if (
                    result?.meta
                        ?.requestStatus ===
                    "fulfilled"
                ) {
                    await loadQuotations();
                }
            } finally {
                setActiveAction(
                    null
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | DELETE QUOTATION
    |--------------------------------------------------------------------------
    */

    const handleDelete =
        async (
            quotation
        ) => {
            const quotationId =
                getQuotationId(
                    quotation
                );

            if (!quotationId) {
                return;
            }

            const quotationNumber =
                getQuotationNumber(
                    quotation
                );

            const approved =
                window.confirm(
                    `Delete ${quotationNumber}?\n\nThis action cannot be undone.`
                );

            if (!approved) {
                return;
            }

            setActiveAction({
                id:
                    quotationId,

                type:
                    "delete",
            });

            try {
                const result =
                    await dispatch(
                        deleteQuotation({
                            documentId:
                                quotationId,
                        })
                    );

                if (
                    result?.meta
                        ?.requestStatus ===
                    "fulfilled"
                ) {
                    await loadQuotations();
                }
            } finally {
                setActiveAction(
                    null
                );
            }
        };

    const handleOpenConversion = (
        quotation
    ) => {
        setConversionDialog({
            open: true,
            document: quotation,
        });
    };
    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
            <div className="quotation-page">
                <div className="quotation-page-header">
                    <div className="quotation-page-heading">
                        <div className="quotation-page-heading-icon">
                            <FiFileText />
                        </div>

                        <div>
                            <h1>
                                Quotations
                            </h1>

                            <p>
                                Create and manage customer
                                quotations, confirmations and
                                document conversions.
                            </p>
                        </div>
                    </div>

                    <div className="quotation-page-header-actions">
                        <button
                            type="button"
                            className="quotation-page-refresh-button"
                            onClick={
                                handleRefresh
                            }
                            disabled={
                                listLoading
                            }
                        >
                            <FiRefreshCw
                                className={
                                    listLoading
                                        ? "quotation-page-spin"
                                        : ""
                                }
                            />

                            <span>
                                Refresh
                            </span>
                        </button>

                        <button
                            type="button"
                            className="quotation-page-create-button"
                            onClick={
                                handleCreateQuotation
                            }
                        >
                            <FiPlus />

                            <span>
                                Create Quotation
                            </span>
                        </button>
                    </div>
                </div>

                {operation?.error && (
                    <div className="quotation-page-alert quotation-page-alert-error">
                        {typeof operation.error ===
                            "string"
                            ? operation.error
                            : operation.error
                                ?.message ||
                            "Unable to complete the quotation operation."}
                    </div>
                )}

                {operation?.success &&
                    operation?.message && (
                        <div className="quotation-page-alert quotation-page-alert-success">
                            {
                                operation.message
                            }
                        </div>
                    )}

                <div className="quotation-summary-grid">
                    {summaryCards.map(
                        (
                            card
                        ) => (
                            <div
                                key={
                                    card.key
                                }
                                className={[
                                    "quotation-summary-card",
                                    card.className,
                                    summaryLoading
                                        ? "quotation-summary-card-loading"
                                        : "",
                                ]
                                    .filter(
                                        Boolean
                                    )
                                    .join(" ")}
                            >
                                <span>
                                    {card.label}
                                </span>

                                {summaryLoading ? (
                                    <div className="quotation-summary-skeleton" />
                                ) : (
                                    <strong
                                        className={
                                            card.isAmount
                                                ? "quotation-summary-amount"
                                                : ""
                                        }
                                    >
                                        {
                                            card.value
                                        }
                                    </strong>
                                )}
                            </div>
                        )
                    )}
                </div>

                <QuotationFilter
                    filters={
                        filters
                    }
                    dropdowns={
                        dropdowns
                    }
                    loading={
                        listLoading
                    }
                    onChange={
                        handleFilterChange
                    }
                    onApply={
                        handleApplyFilters
                    }
                    onReset={
                        handleResetFilters
                    }
                />

                <QuotationTable
                    quotations={
                        quotations
                    }
                    loading={
                        listLoading
                    }
                    pagination={
                        pagination
                    }
                    activeAction={
                        activeAction
                    }
                    onView={
                        handleView
                    }
                    onEdit={
                        handleEdit
                    }
                    onConfirm={
                        handleConfirm
                    }
                    onCancel={
                        handleCancel
                    }
                    onDuplicate={
                        handleDuplicate
                    }
                    onConvert={
                        handleOpenConversion
                    }
                    onPrint={
                        handlePrint
                    }
                    onDelete={
                        handleDelete
                    }
                    onPageChange={
                        handlePageChange
                    }
                    onLimitChange={
                        handleLimitChange
                    }
                    onCreateQuotation={
                        handleCreateQuotation
                    }
                />

                <DocumentConversionDialog
                    open={
                        conversionDialog.open
                    }
                    document={
                        conversionDialog.document
                    }
                    sourceType="QUOTATION"
                    allowedTypes={[
                        "PROFORMA_INVOICE",
                        "SALE_ORDER",
                        "SALE_INVOICE",
                    ]}
                    onClose={() =>
                        setConversionDialog({
                            open: false,
                            document: null,
                        })
                    }
                    onSuccess={() => {
                        setConversionDialog({
                            open: false,
                            document: null,
                        });

                        loadQuotations();
                    }}
                />
            </div>
    );
};

export default QuotationPage;